/**
 * AddParcelModal — create a new, blank Parcel for a project.
 *
 * Opens via CustomEvent('open-add-parcel-modal', { detail: { projectId } }), dispatched by the
 * Parcels section's own ⋯ menu (outline.js's buildSectionMenu, mfn 'addParcel') — available
 * wherever the Parcels section renders (Reference > Design > Parcels, and the Pre-design
 * Research milestone's own Parcels branch, MilestoneSections.jsx).
 *
 * On open, fetches the project's existing parcels to (a) show a live count and (b) prefill
 * Project Parcel Number with the next available number, mirroring AddConceptModal's
 * auto-numbered-default pattern. APN is optional — a parcel's research (zone, jurisdiction,
 * setbacks, etc.) is filled in afterward via the parcel's own detail zoom / Research Tool.
 *
 * On success, pushes the new record into ctx.parcelsCache (if already loaded) and dispatches
 * both 'outline-render' (outline engine + MilestoneSections, which reads ctx.parcelsCache
 * directly) and 'parcels-updated' (DesignSection.jsx listens for this to refresh its own local
 * parcels state, mirroring 'concepts-updated').
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { getParcels, addParcel } from '../services/parcels.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const helpStyle = {
  fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)',
  lineHeight: 1.45, marginTop: 5,
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 13,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const monoInputStyle = { ...inputStyle, fontFamily: '"DM Mono",monospace' }

export default function AddParcelModal() {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [count, setCount] = useState(null)      // existing parcel count — null while resolving
  const [parcelNum, setParcelNum] = useState('')
  const [numTouched, setNumTouched] = useState(false)
  const [apn, setApn] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.projectId || ''
      setProjectId(pid)
      setCount(null)
      setParcelNum('')
      setNumTouched(false)
      setApn('')
      setError(null)
      setSubmitting(false)
      setOpen(true)
      if (pid) {
        getParcels(pid).then(records => setCount(records.length)).catch(() => setCount(0))
      } else {
        setCount(0)
      }
    }
    window.addEventListener('open-add-parcel-modal', onOpen)
    return () => window.removeEventListener('open-add-parcel-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Auto-numbered default (next Project Parcel Number) once the count resolves — only while the
  // user hasn't typed anything yet, so a slow fetch never clobbers what they've already entered.
  useEffect(() => {
    if (count != null && !numTouched) setParcelNum(String(count + 1))
  }, [count, numTouched])

  async function handleSubmit() {
    if (!projectId || !parcelNum.trim() || submitting) return
    setSubmitting(true); setError(null)
    try {
      const fields = { 'Project Parcel ID': Number(parcelNum) }
      if (apn.trim()) fields['APN'] = apn.trim()
      const created = await addParcel(projectId, fields)
      if (Array.isArray(ctx.parcelsCache)) ctx.parcelsCache = [...ctx.parcelsCache, created]
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
      window.dispatchEvent(new CustomEvent('parcels-updated', { detail: { projectId } }))
    } catch (ex) {
      setError(ex.message || 'Failed to create parcel'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = !!parcelNum.trim() && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddParcelModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 420, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            Add Parcel{count != null && <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}> · #{count + 1}</span>}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          <div>
            <label style={labelStyle}>Project Parcel Number</label>
            <input
              style={monoInputStyle} type="number" min="1" value={parcelNum} autoFocus
              onChange={e => { setNumTouched(true); setParcelNum(e.target.value) }}
              placeholder={count != null ? String(count + 1) : '1, 2, 3…'}
            />
          </div>
          <div>
            <label style={labelStyle}>APN <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
            <input
              style={inputStyle} value={apn} onChange={e => setApn(e.target.value)}
              placeholder="Assessor's Parcel Number"
            />
            <div style={helpStyle}>
              Leave blank if not known yet — zone, jurisdiction, setbacks, and every other research
              field are filled in afterward from the parcel's own detail view or the Research Tool.
            </div>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : 'Add Parcel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
